import { useState, useRef, useEffect } from "react";
import { ImagePlus, Reply, Send, X } from "lucide-react";
import type { MentionDetail, Message } from "@/lib/types";

const BODY_LIMIT = 200;

function applyMentionHighlights(
  text: string,
  mentionDetails: MentionDetail[],
): React.ReactNode[] {
  if (!mentionDetails?.length) return [text];
  const escaped = mentionDetails.map((m) =>
    m.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  );
  const pattern = new RegExp(`(@(?:${escaped.join("|")}))`, "g");
  const parts = text.split(pattern);
  return parts.map((part, i) => {
    const hit = mentionDetails.find((m) => part === `@${m.name}`);
    if (hit) {
      return (
        <span
          key={i}
          className="rounded bg-blue-100 dark:bg-blue-950 px-1 text-blue-700 dark:text-blue-300 font-medium"
        >
          {part}
        </span>
      );
    }
    return part;
  });
}

export function TruncatedBody({
  text,
  mentionDetails = [],
}: {
  text: string;
  mentionDetails?: MentionDetail[];
}) {
  const [expanded, setExpanded] = useState(false);
  const display =
    !expanded && text.length > BODY_LIMIT
      ? `${text.slice(0, BODY_LIMIT)}…`
      : text;
  const nodes = applyMentionHighlights(display, mentionDetails);
  return (
    <p className="whitespace-pre-wrap break-words overflow-hidden">
      {nodes}
      {text.length > BODY_LIMIT && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="ml-1 text-xs font-medium underline opacity-70 hover:opacity-100"
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </p>
  );
}

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Check } from "lucide-react";
import { useThread, usePostMessage, useMarkRead } from "@/hooks/useThread";
import { useThreadSSE } from "@/hooks/useThreadSSE";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/components/ui/use-toast";
import { formatDateTime } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { usersApi } from "@/lib/api";
import { CommentImages } from "@/components/request/CommentImages";

// Screenshots in a comment — kept in step with the backend's limits.
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const MAX_IMAGES = 5;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
interface MessageThreadProps {
  requestId: string;
  internalOnly?: boolean;
  disabled?: boolean;
}

interface UserSuggestion {
  oid: string;
  email: string;
  display_name: string;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function MessageThread({
  requestId,
  internalOnly = false,
  disabled = false,
}: MessageThreadProps) {
  const {
    data: messages = [],
    isLoading,
    hasEarlier,
    loadEarlier,
    loadingEarlier,
  } = useThread(requestId);
  const { mutate: postMessage, isPending } = usePostMessage(requestId);
  const { mutate: markRead } = useMarkRead(requestId);
  const { user, isReadOnly, canSubmit, isReviewer } = useAuth();
  const isPM = isReviewer;
  const { toast } = useToast();
  const [body, setBody] = useState("");
  const [isInternal, setIsInternal] = useState(internalOnly);
  const [mentions, setMentions] = useState<MentionDetail[]>([]);
  const [images, setImages] = useState<File[]>([]);
  // Quote-reply: the message being replied to, and the one briefly
  // highlighted after jumping to it from a quote.
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [suggestions, setSuggestions] = useState<UserSuggestion[]>([]);
  const [mentionQuery, setMentionQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Track which message IDs we've already sent a mark-read for this session
  const markedReadRef = useRef<Set<string>>(new Set());

  // Connect SSE for real-time delivery
  useThreadSSE(requestId);

  // Mark messages from others as read when they become visible
  useEffect(() => {
    if (!user?.email || messages.length === 0) return;
    for (const msg of messages) {
      if (
        msg.author_email !== user.email &&
        !markedReadRef.current.has(msg.id)
      ) {
        markedReadRef.current.add(msg.id);
        markRead(msg.id);
      }
    }
  }, [messages, user?.email, markRead]);

  const getMentionContext = (text: string, cursorPos: number) => {
    const beforeCursor = text.substring(0, cursorPos);
    const lastAtIndex = beforeCursor.lastIndexOf("@");

    if (lastAtIndex === -1) return null;

    const afterAt = beforeCursor.substring(lastAtIndex + 1);
    if (afterAt.includes(" ")) return null;

    return { query: afterAt, atIndex: lastAtIndex };
  };

  const handleBodyChange = async (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const newBody = e.target.value;
    const cursorPos = e.target.selectionStart;
    setBody(newBody);

    const context = getMentionContext(newBody, cursorPos);
    if (context && context.query.length > 0) {
      setMentionQuery(context.query);
      setShowSuggestions(true);
      try {
        const users = await usersApi.list(context.query);
        const filtered = users.filter(
          (u) => !mentions.some((m) => m.oid === u.oid),
        );
        setSuggestions(filtered);
      } catch (err) {
        setSuggestions([]);
      }
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  const handleUserSelect = (user: UserSuggestion) => {
    const cursorPos = textareaRef.current?.selectionStart || 0;
    const beforeCursor = body.substring(0, cursorPos);
    const lastAtIndex = beforeCursor.lastIndexOf("@");

    if (lastAtIndex === -1) return;

    const beforeAt = body.substring(0, lastAtIndex);
    const afterCursor = body.substring(cursorPos);

    const newBody = `${beforeAt}@${user.display_name} ${afterCursor}`;
    setBody(newBody);
    setMentions([
      ...mentions,
      { oid: user.oid, name: user.display_name, email: user.email },
    ]);
    setShowSuggestions(false);
    setSuggestions([]);
    setMentionQuery("");
  };

  // Object URLs for the thumbnails under the comment box.
  useEffect(() => {
    const urls = images.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [images]);

  const addImages = (files: File[]) => {
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const accepted: File[] = [];
    for (const f of files) {
      const type = f.type || "image/png";
      if (!IMAGE_TYPES.includes(type)) {
        toast({
          title: "Unsupported image",
          description: "Use PNG, JPEG, GIF or WebP.",
          variant: "destructive",
        });
        continue;
      }
      if (f.size > MAX_IMAGE_BYTES) {
        toast({
          title: "Image too large",
          description: `${f.name || "Screenshot"} is over 10 MB.`,
          variant: "destructive",
        });
        continue;
      }
      // Clipboard images arrive as "image.png"; give them a readable name.
      const name =
        !f.name || f.name === "image.png"
          ? `screenshot-${stamp}-${accepted.length + 1}.${type.split("/")[1]}`
          : f.name;
      accepted.push(new window.File([f], name, { type }));
    }
    const room = MAX_IMAGES - images.length;
    if (accepted.length > room) {
      toast({
        title: `Up to ${MAX_IMAGES} images per comment`,
        variant: "destructive",
      });
    }
    setImages([...images, ...accepted.slice(0, Math.max(room, 0))]);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e.clipboardData.items)
      .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter((f): f is File => f !== null);
    if (files.length === 0) return; // plain text paste
    // Keep the page-level paste handler from also attaching it to the request.
    e.preventDefault();
    e.stopPropagation();
    addImages(files);
  };

  const handleDrop = (e: React.DragEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("image/"),
    );
    if (files.length === 0) return;
    e.preventDefault();
    addImages(files);
  };

  const canReply = !(disabled || isReadOnly);

  const startReply = (msg: Message) => {
    setReplyTo(msg);
    // The quote would expose an internal note, so the reply stays internal.
    if (msg.is_internal) setIsInternal(true);
    textareaRef.current?.focus();
  };

  const jumpTo = async (id: string) => {
    // The original may be on an earlier page that isn't loaded yet.
    let target = document.getElementById(`msg-${id}`);
    for (let i = 0; !target && hasEarlier && i < 10; i++) {
      if (!(await loadEarlier())) break;
      await new Promise((r) => setTimeout(r, 50)); // let it render
      target = document.getElementById(`msg-${id}`);
    }
    target?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    setHighlightId(id);
    window.setTimeout(
      () => setHighlightId((cur) => (cur === id ? null : cur)),
      2000,
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // A comment needs text, a screenshot, or both.
    if ((!body || !body.trim()) && images.length === 0) {
      toast({
        title: "Invalid Message",
        description: "Message cannot be empty or contain only spaces",
        variant: "destructive",
      });
      return;
    }

    // Sanitize and send
    postMessage(
      {
        body: body.trim(),
        is_internal: isInternal,
        mentions: mentions.map((m) => m.oid),
        images,
        reply_to_id: replyTo?.id,
      },
      {
        onSuccess: () => {
          setBody("");
          setMentions([]);
          setImages([]);
          setReplyTo(null);
          toast({ title: "Message sent" });
        },
        onError: (err) =>
          toast({
            title: "Failed to send message",
            description: err.message,
            variant: "destructive",
          }),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-4">
        {messages.length === 0 && (
          <p className="text-center text-sm text-muted-foreground py-6">
            No messages yet
          </p>
        )}
        {[...messages]
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          )
          .map((msg) => {
            const isOwn = msg.author_email === user?.email;
            const isClarificationQ =
              msg.message_type === "clarification_question";
            const isClarificationR =
              msg.message_type === "clarification_response";
            const isStatusChange = msg.message_type === "status_change";

            if (isStatusChange) {
              return (
                <div
                  key={msg.id}
                  className="flex flex-col items-center gap-1 py-1"
                >
                  <div className="flex items-center gap-3 w-full">
                    <div className="flex-1 border-t border-dashed border-muted-foreground/30" />
                    <div className="rounded-full border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950 px-3 py-1 text-xs text-blue-700 dark:text-blue-300 text-center whitespace-pre-wrap max-w-[70%]">
                      {msg.body}
                    </div>
                    <div className="flex-1 border-t border-dashed border-muted-foreground/30" />
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {msg.author_name} · {formatDateTime(msg.created_at)}
                  </p>
                </div>
              );
            }

            return (
              <div
                key={msg.id}
                id={`msg-${msg.id}`}
                className={cn(
                  "flex gap-3 rounded-lg transition-shadow",
                  msg.is_internal && "opacity-75",
                  isOwn && "flex-row-reverse",
                  highlightId === msg.id && "ring-2 ring-primary/60",
                )}
              >
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback className="bg-muted text-xs">
                    {getInitials(msg.author_name)}
                  </AvatarFallback>
                </Avatar>
                <div
                  className={cn(
                    "max-w-[75%] space-y-1",
                    isOwn && "items-end flex flex-col",
                  )}
                >
                  <div
                    className={cn(
                      "rounded-lg px-3 py-2 text-sm",
                      isClarificationQ
                        ? "bg-blue-50 border border-blue-200 text-blue-900 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-200"
                        : isClarificationR
                          ? "bg-teal-50 border border-teal-200 text-teal-900 dark:bg-teal-950 dark:border-teal-800 dark:text-teal-200"
                          : isOwn
                            ? "bg-primary text-primary-foreground"
                            : msg.is_internal
                              ? "bg-amber-50 border border-amber-200 text-amber-900 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-200"
                              : "bg-muted text-foreground",
                    )}
                  >
                    {msg.reply_to ? (
                      <button
                        type="button"
                        onClick={() => void jumpTo(msg.reply_to!.id)}
                        className="mb-1.5 block w-full rounded border-l-2 border-current bg-black/5 dark:bg-white/10 px-2 py-1 text-left text-xs opacity-90 hover:opacity-100"
                        aria-label={`Replying to ${msg.reply_to.author_name}: show original message`}
                      >
                        <span className="font-semibold">
                          {msg.reply_to.author_name}
                        </span>
                        <span className="block truncate">
                          {msg.reply_to.excerpt}
                        </span>
                      </button>
                    ) : msg.reply_to_id ? (
                      <p className="mb-1.5 border-l-2 border-current px-2 text-xs italic opacity-70">
                        Original message unavailable
                      </p>
                    ) : null}
                    {msg.body && (
                      <TruncatedBody
                        text={msg.body}
                        mentionDetails={msg.mention_details}
                      />
                    )}
                    <CommentImages
                      requestId={requestId}
                      images={msg.attachments ?? []}
                    />
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground flex-wrap">
                    <span>{msg.author_name}</span>
                    {isClarificationQ && (
                      <span className="rounded bg-blue-100 dark:bg-blue-950 px-1 text-blue-700 dark:text-blue-300">
                        Clarification Request
                      </span>
                    )}
                    {isClarificationR && (
                      <span className="rounded bg-teal-100 dark:bg-teal-950 px-1 text-teal-700 dark:text-teal-300">
                        Clarification Response
                      </span>
                    )}
                    {msg.is_internal &&
                      !isClarificationQ &&
                      !isClarificationR && (
                        <span className="rounded bg-amber-100 dark:bg-amber-950 px-1 text-amber-700 dark:text-amber-300">
                          Internal
                        </span>
                      )}
                    <span>·</span>
                    <span>{formatDateTime(msg.created_at)}</span>
                    {canReply && (
                      <button
                        type="button"
                        onClick={() => startReply(msg)}
                        className="ml-1 inline-flex items-center gap-0.5 rounded px-1 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label={`Reply to ${msg.author_name}`}
                      >
                        <Reply className="h-3 w-3" />
                        Reply
                      </button>
                    )}
                    {/* Read receipt: shown only on the sender's own messages */}
                    {isOwn &&
                      msg.read_by.some((r) => r !== msg.author_email) && (
                        <span className="flex items-center gap-0.5 text-emerald-600">
                          <Check className="h-3 w-3" />
                          Read
                        </span>
                      )}
                  </div>
                </div>
              </div>
            );
          })}
        {hasEarlier && (
          <div className="flex justify-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={loadingEarlier}
              onClick={() => void loadEarlier()}
            >
              {loadingEarlier ? "Loading…" : "Load earlier messages"}
            </Button>
          </div>
        )}
      </div>

      {disabled || isReadOnly ? (
        <div className="space-y-2 border-t pt-4">
          <p className="text-sm text-muted-foreground p-3 bg-muted rounded-md">
            {isReadOnly && !disabled
              ? "You have read-only access — posting messages is not permitted."
              : "Conversation is read-only on finalized requests."}
          </p>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="space-y-2 border-t pt-4"
          data-comment-composer
        >
          {replyTo && (
            <div
              className="flex items-start justify-between gap-2 rounded-md border-l-4 border-primary bg-muted px-3 py-2 text-xs"
              aria-label="Replying to"
            >
              <div className="min-w-0">
                <span className="font-semibold">
                  Replying to {replyTo.author_name}
                </span>
                <span className="block truncate text-muted-foreground">
                  {replyTo.body.trim() || "📎 Screenshot"}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setReplyTo(null)}
                className="shrink-0 rounded p-0.5 hover:bg-background"
                aria-label="Cancel reply"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <div className="relative">
            <Textarea
              ref={textareaRef}
              value={body}
              onChange={handleBodyChange}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              onPaste={handlePaste}
              onDrop={handleDrop}
              placeholder="Write a message… (@ to mention, paste a screenshot to attach)"
              rows={3}
              className="resize-y"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute bottom-full left-0 right-0 mb-1 border border-muted-foreground/30 rounded-md bg-white dark:bg-gray-900 shadow-lg z-10">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.oid}
                    type="button"
                    onClick={() => handleUserSelect(suggestion)}
                    className="w-full text-left px-3 py-2 hover:bg-muted text-sm border-b last:border-b-0 flex flex-col gap-0.5"
                  >
                    <span className="font-medium">
                      {suggestion.display_name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {suggestion.email}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {previews.length > 0 && (
            <div
              className="flex flex-wrap gap-2"
              aria-label="Screenshots to send"
            >
              {previews.map((url, i) => (
                <div key={url} className="relative">
                  <img
                    src={url}
                    alt={images[i]?.name}
                    className="h-16 w-auto max-w-[120px] rounded border object-cover"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setImages((cur) => cur.filter((_, j) => j !== i))
                    }
                    className="absolute -right-1.5 -top-1.5 rounded-full bg-background border p-0.5 shadow hover:bg-muted"
                    aria-label={`Remove ${images[i]?.name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept={IMAGE_TYPES.join(",")}
                multiple
                hidden
                onChange={(e) => {
                  addImages(Array.from(e.target.files ?? []));
                  e.target.value = "";
                }}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={images.length >= MAX_IMAGES}
                aria-label="Attach screenshot"
                title="Attach screenshot"
              >
                <ImagePlus className="h-4 w-4" />
              </Button>
              {isPM && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                  <input
                    type="checkbox"
                    checked={isInternal}
                    onChange={(e) => setIsInternal(e.target.checked)}
                    disabled={!!replyTo?.is_internal}
                    className="rounded border border-muted-foreground"
                  />
                  <span>
                    {replyTo?.is_internal
                      ? "Internal — replies to internal notes stay internal"
                      : isInternal
                        ? "Internal note — not visible to requestor"
                        : "Visible to requestor"}
                  </span>
                </label>
              )}
              {!isPM && internalOnly && (
                <span className="text-xs text-amber-600 font-medium">
                  ℹ️ Internal note — not visible to requestor
                </span>
              )}
              {mentions.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {mentions.map((m) => (
                    <span
                      key={m.oid}
                      className="inline-flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-950 px-2 py-1 text-xs text-blue-700 dark:text-blue-300"
                    >
                      @{m.name}
                      <button
                        type="button"
                        onClick={() =>
                          setMentions(mentions.filter((u) => u.oid !== m.oid))
                        }
                        className="hover:text-blue-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <Button
              type="submit"
              size="sm"
              disabled={(!body.trim() && images.length === 0) || isPending}
              className="ml-auto"
            >
              <Send className="mr-2 h-4 w-4" />
              Send
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
